import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AdminPortalUser } from '@/lib/domain';
import { getBackendConfigError } from '@/lib/api/client';
import {
  fetchCurrentAdminProfile,
  sendPasswordReset,
  signInAdmin,
  signOutCurrentUser,
} from '@/lib/api/profiles';

interface ActionResult {
  ok: boolean;
  error?: string;
  message?: string;
}

interface AdminAuthContextValue {
  admin: AdminPortalUser | null;
  isLoggedIn: boolean;
  loading: boolean;
  configError: string;
  login: (email: string, password: string) => Promise<ActionResult>;
  logout: () => Promise<void>;
  sendResetPassword: (email: string) => Promise<ActionResult>;
  refresh: () => Promise<void>;
}

const defaultConfigError = getBackendConfigError() || '';

const AdminAuthContext = createContext<AdminAuthContextValue>({
  admin: null,
  isLoggedIn: false,
  loading: false,
  configError: defaultConfigError,
  login: async () => ({ ok: false, error: defaultConfigError }),
  logout: async () => {},
  sendResetPassword: async () => ({ ok: false, error: defaultConfigError }),
  refresh: async () => {},
});

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminPortalUser | null>(null);
  const [loading, setLoading] = useState(true);
  const configError = defaultConfigError;

  const syncAdmin = async () => {
    setLoading(true);

    try {
      const profile = await fetchCurrentAdminProfile();
      setAdmin(profile);
    } catch {
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void syncAdmin();
  }, []);

  const value = useMemo<AdminAuthContextValue>(
    () => ({
      admin,
      isLoggedIn: Boolean(admin),
      loading,
      configError,
      login: async (email, password) => {
        try {
          const result = await signInAdmin(email, password);
          setAdmin(result.admin);
          return { ok: true };
        } catch (err) {
          return {
            ok: false,
            error: err instanceof Error ? err.message : 'Unable to sign in.',
          };
        }
      },
      logout: async () => {
        await signOutCurrentUser();
        setAdmin(null);
      },
      sendResetPassword: async (email) => {
        try {
          await sendPasswordReset(email);
          return {
            ok: true,
            message: 'Password reset email sent. Please check your inbox.',
          };
        } catch (err) {
          return {
            ok: false,
            error: err instanceof Error ? err.message : 'Unable to send password reset email.',
          };
        }
      },
      refresh: syncAdmin,
    }),
    [admin, configError, loading],
  );

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export const useAdminAuth = () => useContext(AdminAuthContext);
